import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { 
  FileText, 
  MapPin, 
  Clock, 
  Users, 
  Camera, 
  Volume2, 
  Music, 
  MessageSquare, 
  Mic,
  Timer,
  StickyNote,
  Save,
  Edit3,
  Check
} from 'lucide-react'
import { Scene, StoryContent } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { debounce } from 'lodash'

interface StoryEditorProps {
  scene: Scene | undefined
}

const defaultStoryContent: StoryContent = {
  sceneDescription: '',
  soundFx: '',
  music: '',
  dialogue: '',
  voiceOver: '',
  location: '',
  timeOfDay: '',
  characters: [],
  cameraNotes: '',
  duration: '',
  notes: ''
}

interface StoryFieldProps {
  label: string
  icon: React.ReactNode
  value: string
  onChange: (value: string) => void
  placeholder: string
  multiline?: boolean
  color: string
}

const StoryField: React.FC<StoryFieldProps> = ({ 
  label, 
  icon, 
  value, 
  onChange, 
  placeholder, 
  multiline = false,
  color
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleSave = () => {
    onChange(localValue)
    setIsEditing(false)
  }

  const colorClasses = {
    amber: 'border-amber-500/30 bg-amber-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
    cyan: 'border-cyan-500/30 bg-cyan-500/5',
    green: 'border-green-500/30 bg-green-500/5',
    pink: 'border-pink-500/30 bg-pink-500/5',
    orange: 'border-orange-500/30 bg-orange-500/5',
    blue: 'border-blue-500/30 bg-blue-500/5',
    red: 'border-red-500/30 bg-red-500/5',
  }

  const iconColorClasses = {
    amber: 'text-amber-400',
    purple: 'text-purple-400',
    cyan: 'text-cyan-400',
    green: 'text-green-400',
    pink: 'text-pink-400',
    orange: 'text-orange-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
  }

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color as keyof typeof colorClasses]} transition-all hover:border-opacity-50`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className={iconColorClasses[color as keyof typeof iconColorClasses]}>{icon}</span>
          <span className="text-white/80 font-medium text-sm">{label}</span>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 hover:bg-white/10 rounded text-white/40 hover:text-white/70 transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={handleSave}
            className="p-1 hover:bg-green-500/20 rounded text-green-400 hover:text-green-300 transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      
      {isEditing ? (
        multiline ? (
          <textarea
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            placeholder={placeholder}
            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none min-h-[100px]"
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            placeholder={placeholder}
            className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            autoFocus
          />
        )
      ) : (
        <p className={`text-sm ${value ? 'text-white/70' : 'text-white/30 italic'} whitespace-pre-wrap`}>
          {value || placeholder}
        </p>
      )}
    </div>
  )
}

const StoryEditor: React.FC<StoryEditorProps> = ({ scene }) => {
  const { updateStoryContent, loading } = useProjectStore()
  const [storyContent, setStoryContent] = useState<StoryContent>(defaultStoryContent)
  const [isSaving, setIsSaving] = useState(false)
  const [charactersInput, setCharactersInput] = useState('')

  useEffect(() => {
    if (scene?.storyContent) {
      setStoryContent(scene.storyContent)
      setCharactersInput(scene.storyContent.characters?.join(', ') || '')
    } else {
      setStoryContent(defaultStoryContent)
      setCharactersInput('')
    }
  }, [scene?.id, scene?.storyContent])

  // Debounced save function
  const debouncedSave = useCallback(
    debounce(async (sceneId: string, content: Partial<StoryContent>) => {
      setIsSaving(true)
      try {
        await updateStoryContent(sceneId, content)
      } catch (error) {
        console.error('Failed to save story content:', error)
      } finally {
        setIsSaving(false)
      }
    }, 500),
    [updateStoryContent]
  )

  const handleFieldChange = (field: keyof StoryContent, value: string | string[]) => {
    if (!scene) return
    
    const newContent = { ...storyContent, [field]: value }
    setStoryContent(newContent)
    debouncedSave(scene.id, { [field]: value })
  }

  const handleCharactersChange = (value: string) => {
    setCharactersInput(value)
    const characters = value.split(',').map(c => c.trim()).filter(c => c)
    handleFieldChange('characters', characters)
  }

  if (!scene) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Scene Selected</h3>
          <p className="text-white/70">
            Select a scene from the sidebar to edit its story content
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6">
        {/* Save Indicator */}
        {isSaving && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-20 right-6 glass-card px-4 py-2 flex items-center space-x-2 text-amber-300 z-50"
          >
            <Save className="w-4 h-4 animate-pulse" />
            <span className="text-sm">Saving...</span>
          </motion.div>
        )}

        {/* Scene Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-headline text-white mb-1">{scene.title}</h2>
          <p className="text-white/50 text-sm">Scene #{(scene.orderIndex || 0) + 1} • Story Content</p>
        </div>

        {/* Story Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Main Scene Description - Full Width */}
          <div className="lg:col-span-2">
            <StoryField
              label="Scene Description"
              icon={<FileText className="w-4 h-4" />}
              value={storyContent.sceneDescription}
              onChange={(v) => handleFieldChange('sceneDescription', v)}
              placeholder="Describe the main action and content of this scene..."
              multiline
              color="amber"
            />
          </div>

          {/* Location & Time */}
          <StoryField
            label="Location"
            icon={<MapPin className="w-4 h-4" />}
            value={storyContent.location}
            onChange={(v) => handleFieldChange('location', v)}
            placeholder="INT. COFFEE SHOP - DAY"
            color="green"
          />

          <StoryField
            label="Time of Day"
            icon={<Clock className="w-4 h-4" />}
            value={storyContent.timeOfDay}
            onChange={(v) => handleFieldChange('timeOfDay', v)}
            placeholder="Morning / Afternoon / Night"
            color="blue"
          />

          {/* Characters */}
          <div className="lg:col-span-2">
            <div className="rounded-lg border p-4 border-pink-500/30 bg-pink-500/5">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="w-4 h-4 text-pink-400" />
                <span className="text-white/80 font-medium text-sm">Characters</span>
              </div>
              <input
                type="text"
                value={charactersInput}
                onChange={(e) => handleCharactersChange(e.target.value)}
                placeholder="Enter character names, separated by commas..."
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/50"
              />
              {storyContent.characters && storyContent.characters.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {storyContent.characters.map((char, idx) => (
                    <span key={idx} className="px-2 py-1 bg-pink-500/20 rounded-full text-pink-300 text-xs">
                      {char}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Dialogue - Full Width */}
          <div className="lg:col-span-2">
            <StoryField
              label="Dialogue"
              icon={<MessageSquare className="w-4 h-4" />}
              value={storyContent.dialogue}
              onChange={(v) => handleFieldChange('dialogue', v)}
              placeholder="CHARACTER: What they say..."
              multiline
              color="purple"
            />
          </div>

          {/* Voice Over */}
          <div className="lg:col-span-2">
            <StoryField
              label="Voice Over / Narration"
              icon={<Mic className="w-4 h-4" />}
              value={storyContent.voiceOver}
              onChange={(v) => handleFieldChange('voiceOver', v)}
              placeholder="Narrator or voice-over text..."
              multiline
              color="cyan"
            />
          </div>

          {/* Sound & Music Row */}
          <StoryField
            label="Sound Effects"
            icon={<Volume2 className="w-4 h-4" />}
            value={storyContent.soundFx}
            onChange={(v) => handleFieldChange('soundFx', v)}
            placeholder="Door creaks, footsteps, rain..."
            multiline
            color="orange"
          />

          <StoryField
            label="Music / Score"
            icon={<Music className="w-4 h-4" />}
            value={storyContent.music}
            onChange={(v) => handleFieldChange('music', v)}
            placeholder="Tense orchestral theme, soft piano..."
            multiline
            color="red"
          />

          {/* Camera Notes */}
          <StoryField
            label="Camera Notes"
            icon={<Camera className="w-4 h-4" />}
            value={storyContent.cameraNotes}
            onChange={(v) => handleFieldChange('cameraNotes', v)}
            placeholder="WIDE SHOT, slow push in..."
            color="blue"
          />

          {/* Duration */}
          <StoryField
            label="Duration"
            icon={<Timer className="w-4 h-4" />}
            value={storyContent.duration}
            onChange={(v) => handleFieldChange('duration', v)}
            placeholder="~5 seconds"
            color="green"
          />

          {/* Additional Notes - Full Width */}
          <div className="lg:col-span-2">
            <StoryField
              label="Production Notes"
              icon={<StickyNote className="w-4 h-4" />}
              value={storyContent.notes}
              onChange={(v) => handleFieldChange('notes', v)}
              placeholder="Any additional production notes, references, or ideas..."
              multiline
              color="amber"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default StoryEditor

